/* Textarea key handling for the workbench (CR-020 / Milestone B).
 *
 * Python is indentation-significant and the shipped editor was a bare
 * `<textarea>`, where Tab moves focus to the next control and leaves the buffer
 * untouched — so there was no way to indent at all. This is the smallest thing
 * that makes writing Python possible without pulling in an editor library.
 *
 * Pure string-and-offset functions, deliberately: the whole point is that the
 * indent rules can be tested without a DOM, a browser, or a keyboard.
 */

export const INDENT = '  ';

export interface EditState {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/** Offset of the start of the line containing `pos`. */
function lineStart(value: string, pos: number): number {
  const nl = value.lastIndexOf('\n', pos - 1);
  return nl === -1 ? 0 : nl + 1;
}

/** Offsets of every line start covered by the selection, ascending. */
function selectedLineStarts(s: EditState): number[] {
  const first = lineStart(s.value, s.selectionStart);
  const starts = [first];
  // A selection ending exactly at a line start does not include that line.
  const end = s.selectionEnd > s.selectionStart && s.value[s.selectionEnd - 1] === '\n'
    ? s.selectionEnd - 1
    : s.selectionEnd;
  for (let i = first; i < end; i++) {
    if (s.value[i] === '\n') starts.push(i + 1);
  }
  return starts;
}

const spansLines = (s: EditState): boolean =>
  s.selectionStart !== s.selectionEnd
  && s.value.slice(s.selectionStart, s.selectionEnd).includes('\n');

/**
 * Tab. A collapsed cursor inserts one indent; a selection spanning lines
 * indents every line it touches, which is what you want when a block moves
 * inside an `if`.
 */
export function indent(s: EditState): EditState {
  if (!spansLines(s)) {
    const value = s.value.slice(0, s.selectionStart) + INDENT + s.value.slice(s.selectionEnd);
    const at = s.selectionStart + INDENT.length;
    return { value, selectionStart: at, selectionEnd: at };
  }
  const starts = selectedLineStarts(s);
  let value = s.value;
  for (let i = starts.length - 1; i >= 0; i--) {
    value = value.slice(0, starts[i]!) + INDENT + value.slice(starts[i]!);
  }
  return {
    value,
    selectionStart: s.selectionStart + INDENT.length,
    selectionEnd: s.selectionEnd + INDENT.length * starts.length,
  };
}

/** Shift+Tab. Removes up to one indent from each touched line; never eats
 *  non-space characters, so hammering it cannot damage code. */
export function outdent(s: EditState): EditState {
  const starts = selectedLineStarts(s);
  let value = s.value;
  let removedBeforeStart = 0;
  let removedTotal = 0;

  for (let i = starts.length - 1; i >= 0; i--) {
    const at = starts[i]!;
    let n = 0;
    while (n < INDENT.length && value[at + n] === ' ') n++;
    if (n === 0) continue;
    value = value.slice(0, at) + value.slice(at + n);
    removedTotal += n;
    if (at < s.selectionStart) removedBeforeStart += n;
    else if (at === s.selectionStart) removedBeforeStart += Math.min(n, s.selectionStart - at);
  }

  return {
    value,
    selectionStart: Math.max(lineStart(value, s.selectionStart - removedBeforeStart),
      s.selectionStart - removedBeforeStart),
    selectionEnd: Math.max(0, s.selectionEnd - removedTotal),
  };
}

/**
 * Enter. Carries the current line's indentation down, and adds one more level
 * after a line that opens a block — `if x:`, `def f():`, `try:` — because that
 * is where the next line always goes.
 */
export function newline(s: EditState): EditState {
  const at = lineStart(s.value, s.selectionStart);
  const line = s.value.slice(at, s.selectionStart);
  const lead = /^[ \t]*/.exec(line)?.[0] ?? '';
  const opensBlock = /:\s*$/.test(line);
  const insert = `\n${lead}${opensBlock ? INDENT : ''}`;
  const value = s.value.slice(0, s.selectionStart) + insert + s.value.slice(s.selectionEnd);
  const pos = s.selectionStart + insert.length;
  return { value, selectionStart: pos, selectionEnd: pos };
}

/** The key row's insert-at-cursor. Replaces any selection, like typing does. */
export function insertText(s: EditState, text: string): EditState {
  const value = s.value.slice(0, s.selectionStart) + text + s.value.slice(s.selectionEnd);
  const pos = s.selectionStart + text.length;
  return { value, selectionStart: pos, selectionEnd: pos };
}
